import { Typography } from '@mui/material';
import { EmployeeCoverage } from '@src/App/Components/General/EmployeeSuggestion/EmployeeSuggestions/EmployeeCoverage';
import React from 'react';

import styles from '../MvpChatAgent.module.css';

export const renderFormattedText = (text: string, isUserMessage: boolean = false): React.ReactNode => {
    const elements: React.ReactNode[] = [];

    // Remove XML tags if present
    text = text.replace(/```xml\s*/i, '').replace(/\s*```/i, '');

    // Find EmployeeCoverage blocks
    const employeeCoverageRegex = /<EmployeeCoverage>([\s\S]*?)<\/EmployeeCoverage>/;
    const match = text.match(employeeCoverageRegex);

    if (match) {
        const [fullMatch, jsonString] = match;
        const before = text.slice(0, match.index);
        const after = text.slice(match.index! + fullMatch.length);

        // Render text before EmployeeCoverage
        elements.push(...renderFormattedTextParts(before, isUserMessage));

        // Parse and render EmployeeCoverage
        try {
            const employees = JSON.parse(jsonString);
            const employeeList: React.ReactNode[] = [];

            if (Array.isArray(employees)) {
                employees.sort((a, b) => b.score - a.score);
                employees.forEach((emp, idx) => {
                    employeeList.push(<EmployeeCoverage key={`emp-${idx}`} inDialog={true} suggestedEmployee={emp} />);
                });
            }

            elements.push(
                <div key="employeeList" className={styles.employeeList}>
                    {employeeList}
                </div>,
            );
        } catch (err) {
            console.error('Error parsing EmployeeCoverage JSON:', err);
            elements.push(
                <Typography key="error" color="error" variant="body1" sx={{ fontSize: '1rem' }}>
                    Fehler beim Anzeigen der Mitarbeitenden.
                </Typography>,
            );
        }

        // Render text after EmployeeCoverage
        elements.push(...renderFormattedTextParts(after, isUserMessage));
    } else {
        // Render all text if no EmployeeCoverage
        elements.push(...renderFormattedTextParts(text, isUserMessage));
    }

    return elements;
};

export const renderFormattedTextParts = (text: string, isUserMessage: boolean = false): React.ReactNode[] => {
    const lines = text.split(/\n+/g);
    let afterHeading = false;

    return lines.map((line, idx) => {
        // Clean up code blocks
        if (/```xml/.test(line)) {
            line = line.replace(/```xml/g, '');
        }
        if (/```/.test(line)) {
            line = line.replace(/```/g, '');
        }

        // H3 heading
        if (/^###\s+/.test(line)) {
            afterHeading = true;
            return (
                <Typography key={`h3-${idx}`} variant="subtitle1" sx={{ mt: 1, fontWeight: 'bold' }}>
                    {line.replace(/^###\s+/, '')}
                </Typography>
            );
        }

        // H2 heading
        if (/^##\s+/.test(line)) {
            afterHeading = true;
            return (
                <Typography key={`h2-${idx}`} variant="h6" sx={{ mt: 1.5, mb: 0.5 }}>
                    {line.replace(/^##\s+/, '')}
                </Typography>
            );
        }

        // Regular text with bold and links
        const parts = line.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
        const paragraphStyle = afterHeading ? { ml: 1, mt: 0.5 } : { mt: 0.5 };
        afterHeading = false;

        return (
            <Typography
                key={`p-${idx}`}
                variant="body1"
                sx={{ ...paragraphStyle, fontSize: '1rem', lineHeight: 1.35 }}
                color={isUserMessage ? 'primary.contrastText' : 'inherit'}
            >
                {parts.map((part, i) => {
                    // Bold text
                    if (part.startsWith('**') && part.endsWith('**')) {
                        return <strong key={i}>{part.slice(2, -2)}</strong>;
                    }

                    // Hyperlink
                    const linkMatch = part.match(/\[(.+)\]\((.+)\)/);
                    if (linkMatch) {
                        return (
                            <a key={i} href={linkMatch[2]} target="_blank" rel="noopener noreferrer">
                                {linkMatch[1]}
                            </a>
                        );
                    }

                    return <React.Fragment key={i}>{part}</React.Fragment>;
                })}
            </Typography>
        );
    });
};

export default { renderFormattedText, renderFormattedTextParts };
